import type { MouseEventHandler } from 'react';
import { useCallback } from 'react';

import { Box } from 'components/Box/Box';
import { Button } from 'components/Button/Button';
import type { ButtonSize } from 'components/Button/Button.types';
import { AnimatedCheckmark } from 'components/Icon/AnimatedCheckmark';
import { useUserDashboardUserHasAppliedToListing } from 'modules/userDashboard/zustand-stores/userDashboardCurrentListingApplicationsStore';
import { scrollIntoView } from 'utils/scrollIntoView';

type Props = {
  atsIsEnabled: boolean;
  listingId: string;
  size?: ButtonSize;
  className?: string;
  tabIndex?: number;
};

export function ApplyButton({
  atsIsEnabled,
  listingId,
  size,
  className,
  tabIndex,
}: Props) {
  const { userHasAppliedToListing } =
    useUserDashboardUserHasAppliedToListing(listingId);

  const onClick: MouseEventHandler<HTMLAnchorElement> = useCallback((e) => {
    const element = document.getElementById('apply');

    if (element) {
      e.preventDefault();
      scrollIntoView(element, {
        highlight: true,
      });
    }
  }, []);

  if (atsIsEnabled && userHasAppliedToListing) {
    return (
      <Button
        type="button"
        data-qa-id="easy-apply-button"
        disabled
        variant="primary"
        size={size || 'large'}
        tabIndex={tabIndex}
      >
        <Box display="flex" alignItems="center" className={className}>
          <Box display="flex" mr="8px">
            <AnimatedCheckmark colorName="brandGreen" size={16} />
          </Box>
          {getText('Applied')}
        </Box>
      </Button>
    );
  }

  return (
    <Button
      type="link"
      data-qa-id="easy-apply-button"
      href="#apply"
      variant="primary"
      onClick={onClick}
      className={className}
      size={size || 'large'}
      tabIndex={tabIndex}
    >
      {getText('Apply')}
    </Button>
  );
}
